import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from "@angular/router";
import { catchError, debounceTime, finalize, throwError } from "rxjs";
import { HttpHeaders } from "@angular/common/http";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { User } from 'src/app/models/user';
import { NgForm } from '@angular/forms';
import { HttpService } from 'src/app/services/http.service';
import { UtilService } from 'src/app/services/util.service';
import { StateService } from 'src/app/services/state.service';


export interface SearchUser {
  user_info_dto: User;
  userInfoList: Array<User>;
}

export interface UserTable {
  lastName: string;
  email: string;
  firstName: string;
  agency: string;
  user_Id: string;
  firm_Name: string
}

@Component({
    selector: 'app-user-search',
    templateUrl: './user-search.component.html',
    styleUrls: ['./user-search.component.scss'],
    standalone: false
})
export class UserSearchComponent implements OnInit, AfterViewInit {
  @ViewChild(MatTable) table!: MatTable<UserTable>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('f', { read: NgForm }) userSearchform!: NgForm;

  displayedColumns: string[] = ['email', 'firstName', 'lastName', 'agencyFirm', 'action'];
  dataSource!: MatTableDataSource<UserTable>;
  disableSearch = false;
  searching = false;
  userUrl ='/admin/manage-accounts/';
  dialogCalled = false;

  constructor(private router: Router,
    private httpService: HttpService, public util: UtilService, public stateService: StateService) { }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<UserTable>();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = this.util.sortingDataAccessor;
    this.userSearchform?.valueChanges!.pipe(
      debounceTime(250)
    ).subscribe(data => this.checkDisableSearch(data));
  }

  private checkDisableSearch(data: any) {
    let count = 0;
    Object.keys(data).forEach(key => data[key] && data[key].toString().length > 0 && count++);
    this.disableSearch = count == 0;
  }

  onEditUser(row: UserTable) {
    this.router.navigate([this.userUrl + row.user_Id]);
  }

  onEdit(e:Event, row: UserTable) {
    e.stopPropagation();
    this.onEditUser(row);
  }

  onDelete(e:Event,row: UserTable) {
    e.stopPropagation();
    this.util.displayDialogMessage("DANGER!!: Deleting User!",
      "You are about to delete the account of " + row.firstName + " " + row.lastName + "<br> from " + row.firm_Name + ".",
      {subtitle:"Are you sure?", confirmText:"DELETE", cancelBtn:true, confirmButtonColor:"warn", cancelButtonColor:"primary"})
      .pipe(finalize(() => { this.dialogCalled = true }))
      .subscribe(res => {
        if (res) {
          let request = {
            isGAO_User: 'N',
            user_Id: row.user_Id
          }
          const params = this.httpService.createHttpParams(request);
          const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
          const options = { ...headers };
          this.httpService.Post('/delete-user', params, options).pipe(
            catchError(err => {
              console.error('Handling error locally.', err);
              return throwError(err);
            })
          ).subscribe(response => {
            if (response) {
              this.util.displayDialogMessage("Success", "You have successfully deleted the user from EPDS.")
                .subscribe(res => {
                  if (res) {
                    this.dataSource.data = this.dataSource.data.filter(eachUser => eachUser.user_Id !== row.user_Id);
                  }
                });
            }
          });
        }
      });
  }

  onReset(e:Event,row: UserTable) {
    e.stopPropagation();
    this.util.displayDialogMessage("Confirmation",
      "You are about to reset the account of " + row.firstName + " " + row.lastName + "<br> from " + row.firm_Name
      + ". " + "Do you want to proceed?", {cancelBtn:true})
      .pipe(finalize(() => { this.dialogCalled = true }))
      .subscribe(res => {
        if (res) {
          this.httpService.Get('/user/reset-account/' + row.user_Id).pipe(
            catchError(err => {
              console.error('Handling error locally.', err);
              return throwError(err);
            })
          ).subscribe(response => {
            if (response) {
              this.util.displayDialogMessage("Success",
                "You have successfully reset the account. <br> An email with instructions will be sent to the user.")
                .pipe(finalize(() => { this.dialogCalled = true })).subscribe();
            }
          });
        }
      });
  }

  onSearch(request: any) {
    const params = this.httpService.createHttpParams(request);
    this.searching = true;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { ...headers };
    this.httpService.Post<SearchUser>('/user-search', params, options).pipe(
      finalize(() => this.searching = false),
      catchError(err => {
        console.log('Handling error locally.', err);
        return throwError(err);
      })
    ).subscribe((response: SearchUser) => {
      let data: UserTable[] = [];
      let userList: Array<User> = response.userInfoList;
      if (userList && userList.length > 0) {
        userList.map((userRet) => {
          let user: UserTable = {
            lastName: userRet.last_Name,
            email: userRet.email,
            firstName: userRet.first_Name,
            agency: userRet.firm_Name,
            user_Id: userRet.user_Id,
            firm_Name: userRet.firm_Name
          }
          data.push(user);
        });
      } else {
        this.util.displayDialogMessage("Error", "No search results were found. Please try changing your search criteria");
      }
      this.dataSource.data = data;
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value ?? '';
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onExport() {
    this.util.exportToExcel(this.dataSource.data, "UserSearchResults");
  }

  onKeypress($event: KeyboardEvent, row: UserTable) {
    if (this.dialogCalled) {
      this.dialogCalled = false;
    } else {
      if ($event.key === "Enter") {
        this.onEdit($event,row);
      }
    }
  }

}
